/**
 * Request Signature Utility
 * 
 * Generate HMAC signature untuk setiap API request.
 * Ini akan mencegah unauthorized access ke API endpoints.
 * 
 * PENTING: SECRET_KEY harus sama dengan yang di backend!
 */

import CryptoJS from 'crypto-js';

// SECRET KEY - HARUS SAMA dengan security.request.signature.secret di application.properties
// Ambil dari window.ENV.REQUEST_SIGNATURE_SECRET atau fallback ke default
const SECRET_KEY = (typeof window !== 'undefined' && window.ENV && window.ENV.REQUEST_SIGNATURE_SECRET) ||
                   process.env.REACT_APP_REQUEST_SIGNATURE_SECRET || 
                   'CHANGE_THIS_TO_RANDOM_SECRET_KEY_MIN_32_CHARS_IN_PRODUCTION_12345678';

/**
 * Generate HMAC-SHA256 signature untuk request
 * 
 * @param {string} method - HTTP method (GET, POST, etc.)
 * @param {string} uri - Request URI path
 * @param {string} queryString - Query string (optional)
 * @param {string} timestamp - Request timestamp
 * @returns {string} Base64 encoded signature
 */
export const generateSignature = (method, uri, queryString, timestamp) => {
  // Build message: METHOD|URI|QUERY|TIMESTAMP
  const message = `${method}|${uri}|${queryString || ''}|${timestamp}`;
  
  // Generate HMAC-SHA256
  const hmac = CryptoJS.HmacSHA256(message, SECRET_KEY);
  
  // Convert to Base64
  return CryptoJS.enc.Base64.stringify(hmac);
};

/**
 * Add signature headers ke axios request config
 * 
 * @param {object} config - Axios request config
 * @returns {object} Modified config with signature headers
 */
export const addSignatureHeaders = (config) => {
  // Generate timestamp
  const timestamp = Date.now().toString();
  
  // Parse URL untuk get path dan query string
  const url = new URL(config.url, window.location.origin);
  const uri = url.pathname;
  const queryString = url.search.substring(1); // Remove '?'
  
  // Generate signature
  const signature = generateSignature(
    config.method.toUpperCase(),
    uri,
    queryString,
    timestamp
  );
  
  // Add headers
  config.headers = config.headers || {};
  config.headers['X-Request-Timestamp'] = timestamp;
  config.headers['X-Request-Signature'] = signature;
  
  return config;
};

/**
 * Validate if signature is required for this request
 * 
 * @param {string} url - Request URL
 * @returns {boolean} true if signature required
 */
export const isSignatureRequired = (url) => {
  // Endpoints yang TIDAK memerlukan signature
  const whitelistPatterns = [
    /^\/auth\//,
    /^\/swagger-ui/,
    /^\/api-docs/,
    /^\/v3\/api-docs/,
    /^\/css\//,
    /^\/js\//,
    /^\/images\//,
    /^\/media\//,
  ];
  
  return !whitelistPatterns.some(pattern => pattern.test(url));
};
